import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MOBILE_BREAKPOINT = 768;

const SIDEBAR_EXPANDED_WIDTH = 290;
const SIDEBAR_COLLAPSED_WIDTH = 86;

const DESKTOP_GAP = 16;
const DESKTOP_BOTTOM_GAP = 16;

const MOBILE_LEFT_GAP = 12;
const MOBILE_BOTTOM_GAP = 12;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function isSidebarCollapsed() {
  if (typeof document === "undefined") return false;

  return (
    document.body.classList.contains("sidebar-collapsed") ||
    document.body.getAttribute("data-sidebar") === "collapsed"
  );
}

function getSidebarWidth() {
  if (typeof window === "undefined") return SIDEBAR_EXPANDED_WIDTH;

  const aside = document.querySelector("aside");

  if (aside) {
    const rect = aside.getBoundingClientRect();

    if (rect.width > 40) {
      return Math.round(rect.width);
    }
  }

  return isSidebarCollapsed()
    ? SIDEBAR_COLLAPSED_WIDTH
    : SIDEBAR_EXPANDED_WIDTH;
}

function getDefaultPosition(width = 56, height = 56, mode = "launcher") {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (vw <= MOBILE_BREAKPOINT) {
    return {
      x: MOBILE_LEFT_GAP,
      y: Math.max(MOBILE_BOTTOM_GAP, vh - height - MOBILE_BOTTOM_GAP),
    };
  }

  const collapsed = isSidebarCollapsed();
  const sidebarWidth = getSidebarWidth();

  if (mode === "window") {
    return {
      x: Math.max(sidebarWidth + DESKTOP_GAP, vw - width - 32),
      y: Math.max(24, vh - height - 48),
    };
  }

  if (collapsed) {
    return {
      x: Math.max(8, Math.round((SIDEBAR_COLLAPSED_WIDTH - width) / 2)),
      y: Math.max(8, vh - height - 168),
    };
  }

  return {
    x: Math.max(8, sidebarWidth + DESKTOP_GAP),
    y: Math.max(8, vh - height - DESKTOP_BOTTOM_GAP),
  };
}

export default function useDraggableFloating({
  width = 56,
  height = 56,
  enabled = true,
  mode = "launcher",
  resetKey = "",
}) {
  const [position, setRawPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 24, y: 24 };
    return getDefaultPosition(width, height, mode);
  });

  const wasDraggedRef = useRef(false);

  const dragRef = useRef({
    dragging: false,
    offsetX: 0,
    offsetY: 0,
  });

  const bounds = useMemo(() => {
    if (typeof window === "undefined") {
      return { minX: 0, minY: 0, maxX: 9999, maxY: 9999 };
    }

    return {
      minX: 8,
      minY: 8,
      maxX: Math.max(8, window.innerWidth - width - 8),
      maxY: Math.max(8, window.innerHeight - height - 8),
    };
  }, [width, height]);

  const setPosition = useCallback(
    (x, y) => {
      setRawPosition({
        x: clamp(x, bounds.minX, bounds.maxX),
        y: clamp(y, bounds.minY, bounds.maxY),
      });
    },
    [bounds]
  );

  const resetPosition = useCallback(() => {
    if (typeof window === "undefined") return;

    const next = getDefaultPosition(width, height, mode);
    wasDraggedRef.current = false;

    setPosition(next.x, next.y);
  }, [width, height, mode, setPosition]);

  useEffect(() => {
    resetPosition();
  }, [resetKey, resetPosition]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onResize = () => {
      setRawPosition((prev) => {
        const safeX = clamp(prev.x, bounds.minX, bounds.maxX);
        const safeY = clamp(prev.y, bounds.minY, bounds.maxY);

        if (window.innerWidth <= MOBILE_BREAKPOINT) {
          const next = getDefaultPosition(width, height, mode);
          return next;
        }

        if (!wasDraggedRef.current) {
          const next = getDefaultPosition(width, height, mode);
          return {
            x: clamp(next.x, bounds.minX, bounds.maxX),
            y: clamp(next.y, bounds.minY, bounds.maxY),
          };
        }

        return { x: safeX, y: safeY };
      });
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [bounds, width, height, mode]);

  useEffect(() => {
    if (!enabled) return;

    const onMove = (e) => {
      if (!dragRef.current.dragging) return;

      const nextX = e.clientX - dragRef.current.offsetX;
      const nextY = e.clientY - dragRef.current.offsetY;

      setRawPosition({
        x: clamp(nextX, bounds.minX, bounds.maxX),
        y: clamp(nextY, bounds.minY, bounds.maxY),
      });
    };

    const onUp = () => {
      dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [bounds, enabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleSidebarChange = () => {
      if (window.innerWidth <= MOBILE_BREAKPOINT) return;
      if (mode !== "launcher") return;

      const next = getDefaultPosition(width, height, mode);

      wasDraggedRef.current = false;

      setRawPosition({
        x: clamp(next.x, bounds.minX, bounds.maxX),
        y: clamp(next.y, bounds.minY, bounds.maxY),
      });
    };

    window.addEventListener(
      "sidebar-state-changed",
      handleSidebarChange
    );

    return () => {
      window.removeEventListener(
        "sidebar-state-changed",
        handleSidebarChange
      );
    };
  }, [bounds, width, height, mode]);

  const bindDragHandle = useCallback(
    (e) => {
      if (!enabled) return;
      if (window.innerWidth <= MOBILE_BREAKPOINT) return;

      wasDraggedRef.current = true;
      dragRef.current.dragging = true;
      dragRef.current.offsetX = e.clientX - position.x;
      dragRef.current.offsetY = e.clientY - position.y;

      e.preventDefault();
    },
    [enabled, position.x, position.y]
  );

  return {
    position,
    setPosition,
    bindDragHandle,
    resetPosition,
  };
}